import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShoppingBag, Package, Truck, DollarSign, Mail, Phone, Globe, ChevronRight, Sparkles } from "lucide-react";

const STATUS_LABELS = {
  "à_contacter": { label: "À contacter", color: "bg-zinc-700 text-zinc-300" },
  "contacté": { label: "Contacté", color: "bg-blue-900/60 text-blue-200 border-blue-700" },
  "wholesale_account_créé": { label: "Compte créé", color: "bg-amber-900/60 text-amber-200 border-amber-700" },
  "actif": { label: "🔥 ACTIF", color: "bg-emerald-900/60 text-emerald-200 border-emerald-600" },
};

const PRIORITY_COLOR = {
  "P0": "border-l-4 border-amber-500",
  "P1": "border-l-4 border-blue-500",
  "P2": "border-l-4 border-zinc-600",
};

export default function BoutiqueAdminModule() {
  const [dashboard, setDashboard] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState({});
  const [outreach, setOutreach] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [expandedSupplier, setExpandedSupplier] = useState(null);

  const loadAll = async () => {
    try {
      const [d, s, p, o] = await Promise.all([
        api.get("/boutique/dashboard"),
        api.get("/boutique/suppliers"),
        api.get("/boutique/products-by-category"),
        api.get("/boutique/outreach-logs"),
      ]);
      setDashboard(d.data);
      setSuppliers(s.data.suppliers);
      setProducts(p.data.categories);
      setOutreach(o.data.logs);
    } catch (e) {
      toast.error("Erreur chargement boutique");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/boutique/suppliers/${id}`, { contact_status: status });
      toast.success("Mis à jour");
      loadAll();
    } catch { toast.error("Erreur"); }
  };

  if (!dashboard) return <div className="p-6 text-zinc-400">Chargement…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="boutique-admin-module">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <ShoppingBag className="w-8 h-8 text-amber-400" />
          Boutique Dojo · Admin
        </h1>
        <p className="text-zinc-400 mt-1">62 produits · 8 fournisseurs · Marge cible 50% · Deadline 1er juin 2026</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-cards">
        <KPI icon={Package} label="Produits actifs" value={dashboard.products.total} color="amber" testid="kpi-products" sub={`${dashboard.products.kits} kits · ${dashboard.products.digital} digitaux`} />
        <KPI icon={Truck} label="Fournisseurs" value={dashboard.suppliers.total} color="blue" testid="kpi-suppliers" sub={`${dashboard.suppliers.contacted} contactés · ${dashboard.suppliers.active} actifs`} />
        <KPI icon={Mail} label="Brouillons outreach" value={dashboard.outreach.drafts_created} color="purple" testid="kpi-outreach" sub="Gmail prospection" />
        <KPI icon={DollarSign} label="Marge moy. catalogue" value={`${dashboard.products.avg_margin_percent}%`} color="emerald" testid="kpi-margin" sub={`${dashboard.products.catalog_value_cad}$ valeur`} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800">
        {["dashboard", "fournisseurs", "produits", "outreach"].map(t => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setActiveTab(t)}
            className={`px-4 py-2 text-sm capitalize transition ${
              activeTab === t
                ? "border-b-2 border-amber-500 text-amber-400 font-semibold"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* DASHBOARD */}
      {activeTab === "dashboard" && (
        <div className="space-y-4" data-testid="dashboard-tab">
          <div className="bg-gradient-to-br from-amber-950/40 to-zinc-900 border border-amber-900/40 rounded-xl p-5 shadow-gold">
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-amber-400"/> Plan Boutique · 1er juin 2026</h2>
            <div className="space-y-2 text-sm">
              <CheckRow done text="Catalogue 62 produits importé (50% marge cible)" />
              <CheckRow done text="8 fournisseurs identifiés (Hatashita, Hayabusa, Matsuru, Warrior Fight, Tatami, Printful, Legacy, Fairtex Thaï)" />
              <CheckRow done text="5 brouillons Gmail prospection créés (vérifie ta boîte Brouillons)" />
              <CheckRow done text="5 kits de départ structurés (Débutant MT · BJJ · Petit Samouraï · Compétiteur · Curriculum)" />
              <CheckRow done text="Scripts d'appel Camille bilingues (FR/EN) générés pour les 8 fournisseurs" />
              <CheckRow text="Tu valides + envoies les 5 brouillons Gmail dans ta boîte Gmail" />
              <CheckRow text="Camille lance les appels aux fournisseurs en parallèle" />
              <CheckRow text="Création compte Printful (CE WEEK-END · pour apparel custom logo)" />
              <CheckRow text="Upload des 4 logos officiels sur Printful + premiers mockups t-shirts" />
              <CheckRow text="Test 1ère commande Stripe → fournisseur dropship" />
              <CheckRow text="Email broadcast aux 87 élèves : 'Boutique en ligne lancée · 10% lancement'" />
            </div>
          </div>
        </div>
      )}

      {/* FOURNISSEURS */}
      {activeTab === "fournisseurs" && (
        <div className="space-y-3" data-testid="suppliers-tab">
          {suppliers.map(s => (
            <div key={s.id} className={`bg-zinc-900 border border-zinc-800 rounded-xl ${PRIORITY_COLOR[s.priority]}`} data-testid={`supplier-${s.id}`}>
              <div className="p-4 cursor-pointer" onClick={() => setExpandedSupplier(expandedSupplier === s.id ? null : s.id)}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-zinc-100">{s.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_LABELS[s.contact_status]?.color || "bg-zinc-800"}`}>
                        {STATUS_LABELS[s.contact_status]?.label || s.contact_status}
                      </span>
                      <span className="text-[10px] text-zinc-500 uppercase">{s.priority}</span>
                    </div>
                    <div className="text-xs text-zinc-400">{s.country} · {s.city}</div>
                    <div className="text-xs text-amber-300/80 mt-1">{(s.brands_carried || []).join(" · ")}</div>
                    <div className="text-xs text-zinc-500 mt-1">{s.product_count} produits dans le catalogue</div>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-zinc-600 transition ${expandedSupplier === s.id ? "rotate-90" : ""}`} />
                </div>
              </div>
              {expandedSupplier === s.id && (
                <div className="border-t border-zinc-800 p-4 space-y-3 bg-zinc-950/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <Info label="Site web" value={<a href={s.website} target="_blank" rel="noreferrer" className="text-amber-400 hover:underline flex items-center gap-1"><Globe className="w-3 h-3"/>{s.website}</a>} />
                    <Info label="Email" value={s.email ? <a href={`mailto:${s.email}`} className="text-amber-400 hover:underline">{s.email}</a> : <span className="text-zinc-600 italic">à trouver</span>} />
                    {s.wholesale_url && <Info label="Wholesale URL" value={<a href={s.wholesale_url} target="_blank" rel="noreferrer" className="text-amber-400 hover:underline text-xs">{s.wholesale_url}</a>} />}
                    <Info label="Dropship" value={String(s.dropship)} />
                    <Info label="MOQ" value={s.moq || "—"} />
                    <Info label="Shipping" value={s.shipping || "—"} />
                    <Info label="Langue" value={s.language || "EN"} />
                  </div>
                  {s.notes && <div className="text-xs text-zinc-400 italic bg-zinc-900 p-2 rounded">💡 {s.notes}</div>}
                  
                  <div className="flex gap-2 flex-wrap">
                    <StatusButton current={s.contact_status} target="à_contacter" onClick={() => updateStatus(s.id, "à_contacter")} />
                    <StatusButton current={s.contact_status} target="contacté" onClick={() => updateStatus(s.id, "contacté")} />
                    <StatusButton current={s.contact_status} target="wholesale_account_créé" onClick={() => updateStatus(s.id, "wholesale_account_créé")} />
                    <StatusButton current={s.contact_status} target="actif" onClick={() => updateStatus(s.id, "actif")} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* PRODUITS */}
      {activeTab === "produits" && (
        <div className="space-y-5" data-testid="products-tab">
          {Object.entries(products).map(([cat, items]) => (
            <div key={cat} data-testid={`category-${cat}`}>
              <h3 className="text-sm uppercase tracking-widest text-amber-400 mb-2 border-b border-zinc-800 pb-1">
                {cat.replace(/_/g, " ")} · <span className="text-zinc-500 normal-case">{items.length} produits</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map(p => (
                  <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 hover:border-amber-700 transition">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <span className="text-2xl">{p.image_emoji}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-zinc-100 truncate">{p.name}</div>
                          <div className="text-[10px] text-zinc-500 mt-1">{(p.tags || []).slice(0,3).join(" · ")}</div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-amber-300 font-bold">{p.price_cad}$</div>
                        <div className="text-[10px] text-emerald-400">{p.margin_percent}% marge</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* OUTREACH */}
      {activeTab === "outreach" && (
        <div className="space-y-3" data-testid="outreach-tab">
          <div className="bg-blue-950/30 border border-blue-900 rounded-lg p-4 text-sm">
            <strong>📧 Brouillons Gmail créés</strong> — Va dans ta boîte Gmail → Brouillons. Valide chaque message et envoie.
          </div>
          {outreach.length === 0 && <div className="text-zinc-500">Aucun outreach pour l'instant.</div>}
          {outreach.map(o => (
            <div key={o.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{o.supplier_name}</div>
                <div className="text-xs text-zinc-400 truncate">{o.email}</div>
                <div className="text-[10px] text-zinc-600">{new Date(o.sent_at).toLocaleString("fr-CA")} · langue {o.language?.toUpperCase()}</div>
              </div>
              <a href={`https://mail.google.com/mail/u/impactkb.bjj@gmail.com/#drafts/${o.draft_id}`} target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300 text-sm whitespace-nowrap">
                Ouvrir →
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, sub, testid }) {
  const colorClass = {
    amber: "text-amber-400 border-amber-900",
    blue: "text-blue-400 border-blue-900",
    purple: "text-purple-400 border-purple-900",
    emerald: "text-emerald-400 border-emerald-900",
  }[color] || "text-zinc-400 border-zinc-800";
  return (
    <div className={`bg-zinc-900 border rounded-lg p-4 ${colorClass}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-600">{label}</div>
      <div className="text-zinc-200">{value}</div>
    </div>
  );
}

function StatusButton({ current, target, onClick }) {
  const isActive = current === target;
  return (
    <button
      onClick={onClick}
      data-testid={`status-btn-${target}`}
      className={`text-xs px-3 py-1 rounded-full border transition ${
        isActive
          ? "bg-amber-500/20 border-amber-500 text-amber-300"
          : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500"
      }`}
    >
      {STATUS_LABELS[target].label}
    </button>
  );
}

function CheckRow({ done, text }) {
  return (
    <div className="flex items-start gap-2">
      <span className={done ? "text-emerald-400" : "text-zinc-600"}>{done ? "✅" : "☐"}</span>
      <span className={done ? "text-zinc-300" : "text-zinc-400"}>{text}</span>
    </div>
  );
}
